import React, { useState, useEffect } from "react";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

/* ─── Inline styles ─────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,400&display=swap');

  :root {
    --bg: #f0f2f7;
    --surface: #ffffff;
    --surface2: #f8f9fc;
    --border: #e2e6f0;
    --accent: #4f67ff;
    --accent-light: #eef0ff;
    --accent-dark: #3a4fd4;
    --success: #22c55e;
    --success-light: #dcfce7;
    --muted: #8591a8;
    --text: #1a2035;
    --text-soft: #4b5673;
    --shadow-sm: 0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04);
    --shadow: 0 4px 16px rgba(79,103,255,.08), 0 1px 4px rgba(0,0,0,.06);
    --shadow-lg: 0 8px 32px rgba(79,103,255,.12), 0 2px 8px rgba(0,0,0,.08);
    --radius: 14px;
    --radius-sm: 8px;
    --transition: .2s cubic-bezier(.4,0,.2,1);
  }

  .sp-wrap * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

  /* ── Sticky Navbar ── */
  .sp-navbar-sticky {
    position: sticky;
    top: 0;
    z-index: 1000;
    box-shadow: 0 2px 12px rgba(0,0,0,.08);
  }

  .sp-wrap {
    min-height: 100vh;
    background: var(--bg);
    padding: 28px 32px 48px;
  }

  /* ── Page Header ── */
  .sp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 28px;
    flex-wrap: wrap;
    gap: 12px;
  }
  .sp-header-left { display: flex; align-items: center; gap: 14px; }
  .sp-icon-box {
    width: 48px; height: 48px;
    background: var(--accent);
    border-radius: var(--radius-sm);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 12px rgba(79,103,255,.35);
  }
  .sp-icon-box svg { color: #fff; width: 22px; height: 22px; }
  .sp-title { font-family: 'Sora', sans-serif; font-size: 1.45rem; font-weight: 700; color: var(--text); line-height: 1.2; }
  .sp-subtitle { font-size: .82rem; color: var(--muted); margin-top: 2px; }

  .sp-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 12px; border-radius: 100px; font-size: .78rem; font-weight: 500;
  }
  .sp-badge-view { background: var(--accent-light); color: var(--accent); }
  .sp-badge-edit { background: #fff3cd; color: #92640a; }
  .sp-badge-add  { background: var(--success-light); color: #166534; }
  .sp-badge-dot  { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }

  /* ── Action Buttons ── */
  .sp-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

  .sp-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 9px 18px; border-radius: var(--radius-sm);
    font-size: .85rem; font-weight: 500; cursor: pointer;
    border: none; transition: all var(--transition);
    text-decoration: none; white-space: nowrap;
  }
  .sp-btn svg { width: 15px; height: 15px; flex-shrink: 0; }

  .sp-btn-primary {
    background: var(--accent); color: #fff;
    box-shadow: 0 2px 8px rgba(79,103,255,.4);
  }
  .sp-btn-primary:hover { background: var(--accent-dark); box-shadow: 0 4px 14px rgba(79,103,255,.5); transform: translateY(-1px); }

  .sp-btn-success {
    background: var(--success); color: #fff;
    box-shadow: 0 2px 8px rgba(34,197,94,.35);
  }
  .sp-btn-success:hover { background: #16a34a; transform: translateY(-1px); }

  .sp-btn-ghost {
    background: var(--surface); color: var(--text-soft);
    border: 1px solid var(--border);
    box-shadow: var(--shadow-sm);
  }
  .sp-btn-ghost:hover { background: var(--surface2); color: var(--text); border-color: #c8cedf; }

  /* ── Section Cards ── */
  .sp-card {
    background: var(--surface);
    border-radius: var(--radius);
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    margin-bottom: 22px;
    overflow: hidden;
    animation: sp-fade-in .35s ease both;
  }
  @keyframes sp-fade-in { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
  .sp-card:nth-child(2) { animation-delay: .05s; }
  .sp-card:nth-child(3) { animation-delay: .10s; }
  .sp-card:nth-child(4) { animation-delay: .15s; }

  .sp-card-header {
    display: flex; align-items: center; gap: 10px;
    padding: 16px 22px;
    background: var(--surface2);
    border-bottom: 1px solid var(--border);
  }
  .sp-card-header-icon {
    width: 32px; height: 32px; border-radius: 8px;
    background: var(--accent-light);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .sp-card-header-icon svg { width: 15px; height: 15px; color: var(--accent); }
  .sp-card-header-title { font-family: 'Sora', sans-serif; font-size: .92rem; font-weight: 600; color: var(--text); }

  .sp-card-body { padding: 24px 22px; }

  /* ── Grid ── */
  .sp-grid { display: grid; gap: 20px; }
  .sp-grid-4 { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); }
  .sp-grid-3 { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
  .sp-grid-2 { grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }

  /* ── Form Fields ── */
  .sp-field { display: flex; flex-direction: column; gap: 6px; }
  .sp-label {
    font-size: .78rem; font-weight: 600; color: var(--text-soft);
    text-transform: uppercase; letter-spacing: .05em;
  }
  .sp-input, .sp-select, .sp-textarea {
    width: 100%;
    padding: 10px 13px;
    border: 1.5px solid var(--border);
    border-radius: var(--radius-sm);
    font-size: .88rem; font-family: 'DM Sans', sans-serif;
    color: var(--text); background: var(--surface);
    transition: all var(--transition);
    outline: none;
  }
  .sp-input:focus, .sp-select:focus, .sp-textarea:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(79,103,255,.12);
  }
  .sp-input:disabled, .sp-select:disabled, .sp-textarea:disabled {
    background: var(--surface2); color: var(--text-soft);
    border-color: var(--border); cursor: default;
  }
  .sp-textarea { resize: vertical; min-height: 80px; }
  .sp-view-value {
    padding: 10px 13px;
    font-size: .88rem; font-family: 'DM Sans', sans-serif;
    color: var(--text); background: var(--surface2);
    border: 1.5px solid var(--border);
    border-radius: var(--radius-sm);
    min-height: 40px;
  }

  /* ── Logo Zone ── */
  .sp-logo-zone {
    display: flex; flex-direction: column; align-items: center;
    gap: 12px; padding: 20px 16px;
    border: 2px dashed var(--border); border-radius: var(--radius);
    background: var(--surface2); text-align: center;
    transition: border-color var(--transition);
  }
  .sp-logo-zone:hover { border-color: var(--accent); }
  .sp-logo-img {
    width: 96px; height: 96px; object-fit: contain;
    border-radius: var(--radius-sm);
    border: 1.5px solid var(--border);
    background: #fff; padding: 6px;
    box-shadow: var(--shadow-sm);
  }
  .sp-logo-placeholder {
    width: 96px; height: 96px; border-radius: var(--radius-sm);
    background: var(--accent-light);
    display: flex; align-items: center; justify-content: center;
  }
  .sp-logo-placeholder svg { width: 36px; height: 36px; color: var(--accent); opacity: .5; }
  .sp-logo-label { font-size: .78rem; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: .05em; }
  .sp-file-input { display: none; }
  .sp-file-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: var(--radius-sm);
    font-size: .8rem; font-weight: 500; cursor: pointer;
    background: var(--accent-light); color: var(--accent);
    border: 1.5px solid rgba(79,103,255,.25);
    transition: all var(--transition);
  }
  .sp-file-btn:hover { background: var(--accent); color: #fff; }
  .sp-file-btn svg { width: 13px; height: 13px; }

  /* ── FY Row ── */
  .sp-fy-row { display: flex; gap: 8px; flex-wrap: wrap; }
  .sp-fy-row .sp-select { flex: 1; min-width: 100px; }
  .sp-fy-display {
    margin-top: 6px; font-size: .82rem; font-weight: 500;
    color: var(--accent); display: flex; align-items: center; gap: 5px;
  }
  .sp-fy-display svg { width: 13px; height: 13px; }

  /* ── Divider ── */
  .sp-divider { border: none; border-top: 1px solid var(--border); margin: 20px 0; }

  @media (max-width: 640px) {
    .sp-wrap { padding: 16px; }
    .sp-header { flex-direction: column; align-items: flex-start; }
  }
`;

/* ─── SVG Icons ─────────────────────────────────────────────────────────── */
const Icon = {
  Building: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 22V12h6v10M3 9h18"/>
    </svg>
  ),
  Mail: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  ),
  Info: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
    </svg>
  ),
  Edit: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  Save: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
    </svg>
  ),
  Plus: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  Back: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  ),
  X: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Upload: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  Image: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
    </svg>
  ),
  Calendar: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const buildFinancialYear = (startMonth, endMonth, startYear) => {
  if (!startMonth || !endMonth || !startYear) return "";
  const endYear = ["January","February","March"].includes(endMonth)
    ? parseInt(startYear) + 1
    : parseInt(startYear);
  return `${startMonth} ${startYear} – ${endMonth} ${endYear}`;
};

/* ─── Field ─────────────────────────────────────────────────────────────── */
const Field = ({ label, name, type = "text", value, onChange, disabled }) => (
  <div className="sp-field">
    <label className="sp-label">{label}</label>
    {disabled
      ? <div className="sp-view-value">{value || <span style={{ color: "#aaa" }}>—</span>}</div>
      : <input
          className="sp-input" type={type}
          name={name} value={value || ""}
          onChange={onChange}
        />
    }
  </div>
);

/* ─── Component ─────────────────────────────────────────────────────────── */
const SettingsPage = () => {
  const token = localStorage.getItem("token");
  const [mode, setMode] = useState("view");
  const navigate = useNavigate();

  const emptyCompany = {
    companyName: "", email: "", leaveEmail: "", smtpPassword: "",
    contactNumber: "", website: "", taxId: "", address: "",
    description: "", financialYear: "", logo: null, logoPreview: null
  };

  const [company, setCompany] = useState(emptyCompany);
  const [savedCompany, setSavedCompany] = useState(emptyCompany);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/company-info/all`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.length > 0) {
          const info = data[0];
          const loaded = {
            ...info, logo: null,
            logoPreview: info.logoPath ? `${API_BASE_URL}/${info.logoPath}` : null
          };
          setCompany(loaded);
          setSavedCompany(loaded);
          setMode("view");
        } else {
          setMode("add");
        }
      });
  }, []);

  useEffect(() => {
    const t = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!t) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("Not authorized."); navigate("/"); return; }
  }, [navigate]);

  const handleChange = e => setCompany({ ...company, [e.target.name]: e.target.value });

  const handleLogoUpload = e => {
    const file = e.target.files[0];
    if (!file) return;
    setCompany({ ...company, logo: file, logoPreview: URL.createObjectURL(file) });
  };

  const handleSave = () => {
    const formData = new FormData();
    formData.append("companyInfo", new Blob([JSON.stringify(company)], { type: "application/json" }));
    if (company.logo) formData.append("logo", company.logo);
    fetch(`${API_BASE_URL}/api/company-info/save`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    })
      .then(r => r.json())
      .then(() => { toast.success("Saved successfully!"); setSavedCompany({ ...company, logo: null }); setMode("view"); });
  };

  const isEditing = mode === "edit" || mode === "add";
  const modeLabel = mode === "add" ? "Add Mode" : mode === "edit" ? "Edit Mode" : "View Mode";
  const modeBadgeClass = mode === "add" ? "sp-badge-add" : mode === "edit" ? "sp-badge-edit" : "sp-badge-view";

  return (
    <>
      <style>{css}</style>
      <div className="sp-navbar-sticky">
        <AdminNavbar />
      </div>


      <div className="sp-wrap">

        {/* ── Header ── */}
        <div className="sp-header">
          <div className="sp-header-left">
            <div className="sp-icon-box"><Icon.Building /></div>
            <div>
              <div className="sp-title">Company Settings</div>
              <div className="sp-subtitle">Manage your organisation profile &amp; configuration</div>
            </div>
          </div>

          <div className="sp-actions">
            <span className={`sp-badge ${modeBadgeClass}`}>
              <span className="sp-badge-dot" />
              {modeLabel}
            </span>

            {mode === "view" && !company.companyName && (
              <button className="sp-btn sp-btn-success" onClick={() => setMode("add")}>
                <Icon.Plus /> Add Company
              </button>
            )}
            {mode === "view" && company.companyName && (
              <button className="sp-btn sp-btn-primary" onClick={() => setMode("edit")}>
                <Icon.Edit /> Edit
              </button>
            )}
            {mode === "view" && (
              <button className="sp-btn sp-btn-ghost" onClick={() => navigate(-1)}>
                <Icon.Back /> Back
              </button>
            )}
            {isEditing && (
              <>
                <button className="sp-btn sp-btn-primary" onClick={handleSave}>
                  <Icon.Save /> Save Changes
                </button>
                <button className="sp-btn sp-btn-ghost" onClick={() => { setCompany(savedCompany); setMode("view"); }}>
                  <Icon.X /> Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Primary Information ── */}
        <div className="sp-card">
          <div className="sp-card-header">
            <div className="sp-card-header-icon"><Icon.Building /></div>
            <span className="sp-card-header-title">Primary Information</span>
          </div>
          <div className="sp-card-body">
            <div className="sp-grid sp-grid-4" style={{ alignItems: "start" }}>

              {/* Logo */}
              <div className="sp-logo-zone">
                <span className="sp-logo-label">Company Logo</span>
                {company.logoPreview
                  ? <img src={company.logoPreview} alt="logo" className="sp-logo-img" />
                  : <div className="sp-logo-placeholder"><Icon.Image /></div>
                }
                {isEditing && (
                  <>
                    <input
                      id="logo-file" type="file" accept="image/*"
                      className="sp-file-input" onChange={handleLogoUpload}
                    />
                    <label htmlFor="logo-file" className="sp-file-btn">
                      <Icon.Upload /> Upload Logo
                    </label>
                  </>
                )}
              </div>

              <Field label="Company Name" name="companyName" value={company.companyName} onChange={handleChange} disabled={!isEditing} />
              <Field label="Email Address" name="email" type="email" value={company.email} onChange={handleChange} disabled={!isEditing} />
              <Field label="Contact Number" name="contactNumber" value={company.contactNumber} onChange={handleChange} disabled={!isEditing} />
            </div>
          </div>
        </div>

        {/* ── Email & Web ── */}
        <div className="sp-card">
          <div className="sp-card-header">
            <div className="sp-card-header-icon"><Icon.Mail /></div>
            <span className="sp-card-header-title">Email &amp; Web Presence</span>
          </div>
          <div className="sp-card-body">
            <div className="sp-grid sp-grid-3">
              <Field label="Leave Notification Email" name="leaveEmail" type="email" value={company.leaveEmail} onChange={handleChange} disabled={!isEditing} />
              <Field label="Website URL" name="website" value={company.website} onChange={handleChange} disabled={!isEditing} />

              {/* Financial Year */}
              <div className="sp-field">
                <label className="sp-label">Financial Year</label>
                <div className="sp-fy-row">
                  <select
                    className="sp-select" value={company.startMonth || ""}
                    disabled={!isEditing}
                    onChange={e => {
                      const startMonth = e.target.value;
                      setCompany({ ...company, startMonth, financialYear: buildFinancialYear(startMonth, company.endMonth, company.startYear) });
                    }}
                  >
                    <option value="">Start</option>
                    {MONTHS.map(m => <option key={m}>{m}</option>)}
                  </select>

                  <select
                    className="sp-select" value={company.endMonth || ""}
                    disabled={!isEditing}
                    onChange={e => {
                      const endMonth = e.target.value;
                      setCompany({ ...company, endMonth, financialYear: buildFinancialYear(company.startMonth, endMonth, company.startYear) });
                    }}
                  >
                    <option value="">End</option>
                    {MONTHS.map(m => <option key={m}>{m}</option>)}
                  </select>

                  <select
                    className="sp-select" value={company.startYear || ""}
                    disabled={!isEditing}
                    onChange={e => {
                      const startYear = e.target.value;
                      setCompany({ ...company, startYear, financialYear: buildFinancialYear(company.startMonth, company.endMonth, startYear) });
                    }}
                  >
                    <option value="">Year</option>
                    {Array.from({ length: 6 }).map((_, i) => {
                      const y = new Date().getFullYear() - 2 + i;
                      return <option key={y}>{y}</option>;
                    })}
                  </select>
                </div>
                {company.financialYear && (
                  <div className="sp-fy-display">
                    <Icon.Calendar /> {company.financialYear}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Other Information ── */}
        <div className="sp-card">
          <div className="sp-card-header">
            <div className="sp-card-header-icon"><Icon.Info /></div>
            <span className="sp-card-header-title">Other Information</span>
          </div>
          <div className="sp-card-body">
            <div className="sp-grid sp-grid-3" style={{ marginBottom: 20 }}>
              <Field label="GST / Tax ID" name="taxId" value={company.taxId} onChange={handleChange} disabled={!isEditing} />
              <Field label="Mail App Password" name="smtpPassword" type="password" value={company.smtpPassword} onChange={handleChange} disabled={!isEditing} />
            </div>
            <hr className="sp-divider" />
            <div className="sp-grid sp-grid-2">
              <div className="sp-field">
                <label className="sp-label">Address</label>
                {!isEditing
                  ? <div className="sp-view-value">{company.address || <span style={{ color: "#aaa" }}>—</span>}</div>
                  : <textarea
                      className="sp-textarea" name="address" rows={3}
                      value={company.address || ""} onChange={handleChange}
                    />
                }
              </div>
              <div className="sp-field">
                <label className="sp-label">Description</label>
                {!isEditing
                  ? <div className="sp-view-value">{company.description || <span style={{ color: "#aaa" }}>—</span>}</div>
                  : <textarea
                      className="sp-textarea" name="description" rows={3}
                      value={company.description || ""} onChange={handleChange}
                    />
                }
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default SettingsPage;